import React, { useState, useEffect } from 'react';
import './ContactsPage.css';
import { FiPhone, FiMail, FiEdit2, FiTrash2, FiPlus } from 'react-icons/fi';

export default function ContactsPage({ apiUrl }) {
  const [contacts, setContacts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'Entrepreneur',
    phone: '',
    email: '',
    company: '',
    speciality: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/contacts`);
      if (!response.ok) throw new Error('Failed to load contacts');
      const data = await response.json();
      setContacts(data);
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Le nom est requis');
      return;
    }

    try {
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId 
        ? `${apiUrl}/contacts/${editingId}`
        : `${apiUrl}/contacts`;

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save contact');
      
      setFormData({
        name: '',
        type: 'Entrepreneur',
        phone: '',
        email: '',
        company: '',
        speciality: '',
        notes: ''
      });
      setEditingId(null);
      setShowForm(false);
      await loadContacts();
    } catch (error) {
      console.error('Error saving contact:', error);
      alert('Erreur lors de la sauvegarde du contact');
    }
  };

  const handleEdit = (contact) => {
    setFormData({
      name: contact.name,
      type: contact.type,
      phone: contact.phone || '',
      email: contact.email || '',
      company: contact.company || '',
      speciality: contact.speciality || '',
      notes: contact.notes || ''
    });
    setEditingId(contact.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contact ?')) {
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/contacts/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to delete contact');
      await loadContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
      alert('Erreur lors de la suppression du contact');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      name: '',
      type: 'Entrepreneur',
      phone: '',
      email: '',
      company: '',
      speciality: '',
      notes: ''
    });
  };

  const getTypeColor = (type) => {
    const colors = {
      'Entrepreneur': '#667eea',
      'Fournisseur': '#10b981',
      'Architecte': '#f59e0b',
      'Ingénieur': '#8b5cf6',
      'Autre': '#6b7280'
    };
    return colors[type] || colors['Autre'];
  };

  return (
    <div className="contacts-page">
      <div className="contacts-header">
        <h2>📋 Gestion des Contacts</h2>
        {!showForm && (
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            <FiPlus /> Nouveau Contact
          </button>
        )}
      </div>

      {showForm && (
        <div className="contact-form-card">
          <h3>{editingId ? 'Modifier le contact' : 'Ajouter un contact'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Nom *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Type</label>
                <select name="type" value={formData.type} onChange={handleChange}>
                  <option>Entrepreneur</option>
                  <option>Fournisseur</option>
                  <option>Architecte</option>
                  <option>Ingénieur</option>
                  <option>Autre</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Téléphone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Entreprise</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Spécialité</label>
                <input
                  type="text"
                  name="speciality"
                  value={formData.speciality}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingId ? 'Mettre à jour' : 'Ajouter'}
              </button>
              <button type="button" className="btn-secondary" onClick={handleCancel}>
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading">Chargement des contacts...</div>
      ) : contacts.length === 0 ? (
        <div className="empty-state">
          <p>Aucun contact pour le moment</p>
          <p className="text-muted">Commencez par ajouter un contact</p>
        </div>
      ) : (
        <div className="contacts-grid">
          {contacts.map(contact => (
            <div key={contact.id} className="contact-card">
              <div className="contact-header">
                <div>
                  <h3>{contact.name}</h3>
                  <span className="type-badge" style={{ backgroundColor: getTypeColor(contact.type) }}>
                    {contact.type}
                  </span>
                </div>
                <div className="contact-actions">
                  <button 
                    className="btn-icon" 
                    onClick={() => handleEdit(contact)}
                    title="Modifier"
                  >
                    <FiEdit2 />
                  </button>
                  <button 
                    className="btn-icon btn-danger" 
                    onClick={() => handleDelete(contact.id)}
                    title="Supprimer"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>

              {contact.company && (
                <p className="contact-company">{contact.company}</p>
              )}

              {contact.speciality && (
                <p className="contact-speciality">📌 {contact.speciality}</p>
              )}

              <div className="contact-info">
                {contact.phone && (
                  <div className="info-item">
                    <FiPhone size={16} />
                    <a href={`tel:${contact.phone}`}>{contact.phone}</a>
                  </div>
                )}
                {contact.email && (
                  <div className="info-item">
                    <FiMail size={16} />
                    <a href={`mailto:${contact.email}`}>{contact.email}</a>
                  </div>
                )}
              </div>

              {contact.notes && (
                <div className="contact-notes">
                  <p>{contact.notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
